import { useCallback, useRef } from "react";

import { useOptionalPostCacheContext } from "@/contexts/post-cache-context";
import {
  DEFAULT_IMAGE_DIMENSIONS,
  getSafeMediaDimensions,
} from "@/utils/media-dimensions";

export const useImageMeta = () => {
  const postCacheContext = useOptionalPostCacheContext();
  const fallbackImageSizeMapRef = useRef<
    Map<
      string,
      {
        width: number;
        height: number;
      }
    >
  >(new Map());
  const postsImageSizeMap =
    postCacheContext?.postsImageSizeMap ?? fallbackImageSizeMapRef.current;

  const checkImageCache = useCallback(
    (url: string) => {
      return postsImageSizeMap.get(url);
    },
    [postsImageSizeMap],
  );

  const getImageMeta = useCallback(
    async (urls: string[]) => {
      const sizes = await Promise.all(
        urls.map(
          (url) =>
            new Promise<{ width: number; height: number }>((resolve) => {
              const sizeFromCache = checkImageCache(url);
              if (sizeFromCache) {
                resolve(sizeFromCache);
              } else {
                const img = new Image();
                img.src = url;
                img.onload = () => {
                  resolve(getSafeMediaDimensions(img));
                };
                img.onerror = () => {
                  resolve(DEFAULT_IMAGE_DIMENSIONS);
                };
              }
            }),
        ),
      );

      for (let i = 0; i < sizes.length; i++) {
        const size = sizes[i];
        if (size) {
          postsImageSizeMap.set(urls[i], size);
        }
      }
    },
    [checkImageCache, postsImageSizeMap],
  );

  return { getImageMeta, checkImageCache };
};
